import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import * as R from 'ramda';
import { reduxForm } from 'redux-form';

// Helpers
import classNames from 'classnames';
import { gtmEventPush } from 'utils/gtm/events';

// Components
import BasicFiltersResults from 'Results/BasicFiltersResults';
import AdvancedFiltersResults from 'Results/AdvancedFiltersResults';
import KeywordSearch from 'Results/KeywordSearch';
import ActiveFilters from 'Results/ActiveFilters';
import SortBy from 'Results/SortBy';
import Icon from 'Common/Icon';

// Styles
import bootstrap from 'vendor/bootstrap/_bootstrap_custom.scss';
import styles from 'components/FilterForm.scss';

const fieldsHaveChanged = (a, b) => !R.equals(a, b);

/**
 * <FilterForm /> component.
 * @return {class} new FilterForm instance
 */
export class FilterForm extends PureComponent<*, *> {
  constructor(props: Object) {
    super(props);
    this.postcodeRef = React.createRef();
    this.timeouts = [];
    this.state = {
      highlight: false,
      showErrorMessage: false, // postcode error message
      showAdvancedFilters: true,
      selected: ''
    };
  }
  /**
   * componentDidMount
   * @return {void}
   */
  componentDidMount() {}

  /* eslint-disable camelcase */
  /**
   * UNSAFE_componentWillReceiveProps
   * @param {object} nextProps - nextProps
   * @return {void}
   */
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      this.props.initialized &&
      fieldsHaveChanged(this.props.fields, nextProps.fields)
    ) {
      const currentPropsWithoutPostCode = R.omit(
        ['postCode', 'lat', 'long'],
        this.props.fields
      );
      const nextPropsWithoutPostCode = R.omit(
        ['postCode', 'lat', 'long'],
        nextProps.fields
      );
      if (
        fieldsHaveChanged(currentPropsWithoutPostCode, nextPropsWithoutPostCode)
      ) {
        this.props.getResults();
      }
    }
    if (this.props.isMobile !== nextProps.isMobile) {
      this.setState({ showAdvancedFilters: !nextProps.isMobile });
    }
  }

  fadeOutAfter: (delay: number) => void = delay => {
    setTimeout(
      () =>
        this.setState({
          showErrorMessage: false
        }),
      delay
    );
  };

  displayPostcodeErrorMessage = (showErrorMessage: boolean) => {
    if (showErrorMessage) {
      this.setState({ showErrorMessage: true, highlight: true });
      this.fadeOutAfter(3000);
    } else {
      this.setState({ showErrorMessage: false });
    }
  };

  scrollToPostCodeRef = e => {
    const { isPostcodeValid } = this.props;

    gtmEventPush({
      method: 'filterFormSearchButtonClick',
      args: {
        isPostcodeValid
      }
    });

    if (!isPostcodeValid) {
      e.preventDefault();
      window.scrollTo(0, this.postcodeRef.offsetTop);
      this.setState({ highlight: true });
    }
  };

  handleSubmit = e => {
    const {
      isPostcodeValid,
      isListingsPage,
      closeFilters,
      handleSubmit
    } = this.props;

    this.scrollToPostCodeRef(e);

    if (isPostcodeValid) {
      (isListingsPage ? closeFilters : handleSubmit)();
    } else {
      if (isListingsPage) {
        closeFilters();
      }
    }
  };

  setPostcodeRef = el => {
    this.postcodeRef = el;
  };

  checkPostcode = event => {
    this.props.checkPostcode(event);
    if (event.target.value) {
      this.setState({ highlight: true });
    }
  };

  toggleAdvancedFilters = () => {
    this.setState((prev, props) => ({
      showAdvancedFilters: !prev.showAdvancedFilters
    }));
  };

  /**
   * setAsSelected
   *
   * @param {string} name - name.
   * @return {void}
   */
  setAsSelected: (name: string | null) => void = (name: string | null) => {
    this.setState({
      selected: name
    });
  };

  /**
   * closeSwoosh
   *
   * @param {object} event - event.
   * @return {void}
   */
  closeSwoosh: (event: SyntheticInputEvent<*>) => void = event => {
    const selectedCheckBoxGroup = document.querySelectorAll(
      '[data-selectedcheckboxgroup=true]'
    )[0];

    if (
      selectedCheckBoxGroup &&
      !selectedCheckBoxGroup.contains(event.target)
    ) {
      this.timeouts.push(setTimeout(() => this.setAsSelected(''), 300));
    }
  };

  /**
   * Renders FilterForm.
   *
   * @return {JSX} rendered FilterForm.
   */
  render(): Element<*> | null {
    const {
      handleSubmit,
      fields,
      makes,
      models,
      priceFrom,
      priceTo,
      isPostcodeValid,
      additonalOptions,
      dealerName,
      fetchUpdatedQuery,
      resultsLength,
      ui,
      isMobile,
      isResultsLoading,
      activeFilters,
      removeActiveFilter,
      resetAllFields,
      resetField,
      closeFilters,
      postcodeVisited
    } = this.props;
    const { highlight, showAdvancedFilters } = this.state;

    // Persist postcode
    if (ui && ui.lastPostCode) {
      fields['postCode'] = ui.lastPostCode;
    }

    const disableSearchButton =
      fields &&
      fields.postCode &&
      fields.postCode.length > 0 &&
      !isPostcodeValid;

    return (
      <div>
        {!isMobile && (
          <SortBy
            fetchUpdatedQuery={fetchUpdatedQuery}
            selectedValue={fields.sortBy}
            isPostcode={fields.postCode}
            displayPostcodeErrorMessage={this.displayPostcodeErrorMessage}
            showErrorMessage={this.state.showErrorMessage}
          />
        )}
        <form
          onSubmit={handleSubmit}
          className={classNames(
            styles.filterForm,
            bootstrap.formInline,
            bootstrap.clearfix
          )}
        >
          <div className={styles.formGroup}>
            <div className={styles.toggleModeBtnContainer}>
              <KeywordSearch
                styles={styles}
                fields={fields}
                resultsLength={resultsLength}
              />
              {isMobile && (
                <button
                  className={styles.closeFilterModeBtn}
                  onClick={closeFilters}
                  title={isPostcodeValid ? '' : 'Postcode is invalid!'}
                >
                  <Icon
                    iconName={'close'}
                    size={'medium'}
                    className={classNames(styles.icon, styles.right)}
                  />
                </button>
              )}
            </div>
            {isMobile && (
              <ActiveFilters
                fields={fields}
                dealerName={dealerName}
                activeFilters={activeFilters}
                removeActiveFilter={removeActiveFilter}
                isMobile={isMobile}
                resetAllFields={resetAllFields}
              />
            )}
            <BasicFiltersResults
              styles={styles}
              makes={makes}
              models={models}
              fields={fields}
              priceFrom={priceFrom}
              priceTo={priceTo}
              resetField={resetField}
              checkPostcode={this.checkPostcode}
              isPostcodeValid={isPostcodeValid}
              isResultsLoading={isResultsLoading}
              setPostcodeRef={this.setPostcodeRef}
              highlight={postcodeVisited || highlight}
              isMobile={isMobile}
              setAsSelected={this.setAsSelected}
              closeSwoosh={this.closeSwoosh}
              currentSelection={this.state.selected}
            />
            <button
              className={classNames(
                bootstrap.btn,
                bootstrap.btnLink,
                bootstrap.btnLg,
                styles.moreOptionsButton
              )}
              type="button"
              onClick={this.toggleAdvancedFilters}
            >
              <Icon
                iconName="more-options"
                size="default"
                className={styles.iconFilter}
              />
              {showAdvancedFilters ? 'More options' : 'Less options'}
              <Icon
                iconName={
                  showAdvancedFilters
                    ? 'chevron-down-small'
                    : 'chevron-up-small'
                }
                size="default"
                className={classNames(styles.iconFilter, styles.arrowIcon)}
              />
            </button>
            {!showAdvancedFilters && (
              <AdvancedFiltersResults
                styles={styles}
                fields={fields}
                additonalOptions={additonalOptions}
                resetField={resetField}
                isMobile={isMobile}
                setAsSelected={this.setAsSelected}
                closeSwoosh={this.closeSwoosh}
                currentSelection={this.state.selected}
              />
            )}
            {isMobile && (
              <div className={classNames(styles.searchButtonContainer)}>
                <button
                  className={classNames(
                    bootstrap.btn,
                    bootstrap.btnSecondary,
                    bootstrap.btnLg,
                    styles.searchButton
                  )}
                  onClick={e => this.handleSubmit(e)}
                  type="submit"
                  disabled={disableSearchButton || !resultsLength}
                  title={isPostcodeValid ? '' : 'Postcode is invalid!'}
                >
                  <span>Search </span>
                  {!isResultsLoading && (
                    <span className={classNames(styles.result)}>
                      {resultsLength && resultsLength.toLocaleString('en')}
                    </span>
                  )}
                  <span> results</span>
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
    );
  }
}

function mapStateToProps(state, props) {
  const searchState = state.form.search || { values: {} };
  const initialValues = props.initialValues || searchState.values;

  return {
    initialValues: {
      postCode: state.ui.lastPostCode,
      isPostCodeValid: Boolean(state.ui.lastPostCode),
      ...initialValues
    },
    ui: state.ui
  };
}

// eslint-disable-next-line no-class-assign
const ReduxFilterForm = reduxForm({
  form: 'search',
  enableReinitialize: false,
  destroyOnUnmount: false
})(FilterForm);

export default connect(mapStateToProps)(ReduxFilterForm);



// WEBPACK FOOTER //
// ./src/Results/FilterForm/index.js